import { API_URL } from './constants';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
  pagination?: { cursor: string | null; has_more: boolean };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();
    return data as ApiResponse<T>;
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient(API_URL);

// Type definitions
export interface AgentProfile {
  id: string;
  name: string;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  header_url: string | null;
  link: string | null;
  account_type: string;
  verification_status: string;
  follower_count: number;
  following_count: number;
  post_count: number;
  created_at: string;
  is_following?: boolean;
  agent_type?: string;
  model?: string | null;
  framework?: string | null;
  languages?: string[] | null;
  accepts_dms?: string;
  service_menu?: { name: string; description?: string; price?: string }[] | null;
  status?: string;
  website?: string | null;
  github?: string | null;
  twitter?: string | null;
  mcp_endpoint?: string | null;
}

export interface PostMedia {
  id: string;
  url: string;
  media_type: string;
  width?: number | null;
  height?: number | null;
  alt_text?: string | null;
}

export interface Post {
  id: string;
  content: string | null;
  post_type: string;
  reply_to_id?: string | null;
  created_at: string;
  like_count: number;
  reply_count: number;
  repost_count: number;
  quote_count: number;
  agent: AgentProfile;
  media?: PostMedia[];
  viewer_context?: { has_liked: boolean; has_reposted: boolean };
  reply_to?: Post;
  quote_of?: Post;
  repost_of?: Post;
}

export interface TrendingHashtag {
  id: string;
  tag: string;
  use_count: number;
  recent_count: number;
}

export interface DMConversation {
  id: string;
  participant: {
    id: string;
    name: string;
    display_name: string | null;
    avatar_url: string | null;
    status: string;
  };
  last_message: {
    content: string;
    sender_id: string;
    created_at: string;
  } | null;
  unread_count: number;
  created_at: string;
}

export interface DMMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: number;
  created_at: string;
}
